import React, { useState, useMemo } from 'react';
import { TrendingUp, Users, DollarSign, Target } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

export function GreekRushROI() {
  const [rushBudget, setRushBudget] = useState<number>(5000);
  const [bidsExtended, setBidsExtended] = useState<number>(40);
  const [bidsAccepted, setBidsAccepted] = useState<number>(32);
  const [annualDues, setAnnualDues] = useState<number>(1200);
  const [averageTenure, setAverageTenure] = useState<number>(3.5); // Years

  const model = useMemo(() => {
    const costPerBid = bidsExtended > 0 ? rushBudget / bidsExtended : 0;
    const costPerNewMember = bidsAccepted > 0 ? rushBudget / bidsAccepted : 0;
    
    const bidAcceptanceRate = bidsExtended > 0 ? (bidsAccepted / bidsExtended) * 100 : 0;
    
    // Lifetime Value (LTV) of a new member
    const lifetimeValuePerMember = annualDues * averageTenure;
    const totalNewRevenue = lifetimeValuePerMember * bidsAccepted;
    
    const netROI = totalNewRevenue - rushBudget;
    const roiPercentage = rushBudget > 0 ? (netROI / rushBudget) * 100 : 0;

    const chartData = [
      { name: 'Accepted', value: bidsAccepted, color: '#10b981' },
      { name: 'Declined', value: bidsExtended - bidsAccepted, color: '#ef4444' }
    ];

    return {
      costPerBid,
      costPerNewMember,
      bidAcceptanceRate,
      lifetimeValuePerMember,
      totalNewRevenue,
      netROI,
      roiPercentage,
      chartData
    };
  }, [rushBudget, bidsExtended, bidsAccepted, annualDues, averageTenure]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-purple-700 to-fuchsia-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <TrendingUp className="w-6 h-6 mr-3 text-purple-300" />
            Rush Budget ROI Calculator
          </h2>
          <p className="text-purple-100 max-w-xl">
            Treat recruitment like an investment. Calculate the lifetime value (LTV) of a new member and determine the true return on investment of your rush week budget.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        
        {/* Controls */}
        <Card className="p-6 space-y-6 md:col-span-1 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
              <DollarSign className="w-4 h-4 mr-2 text-purple-500" /> Total Rush Budget ($)
            </label>
            <Input 
              type="number" 
              value={rushBudget} 
              onChange={(e) => setRushBudget(Number(e.target.value))}
              className="text-lg font-bold"
            />
          </div>
          
          <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
            <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
              <Target className="w-4 h-4 mr-2 text-purple-500" /> Bids Extended
            </label>
            <Input 
              type="number" 
              value={bidsExtended} 
              onChange={(e) => setBidsExtended(Number(e.target.value))}
            />
          </div>
          
          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
              <Users className="w-4 h-4 mr-2 text-emerald-500" /> Bids Accepted
            </label>
            <Input 
              type="number" 
              value={bidsAccepted} 
              onChange={(e) => setBidsAccepted(Math.min(Number(e.target.value), bidsExtended))}
            />
          </div>

          <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
            <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Annual Dues Per Member ($)</label>
            <Input 
              type="number" 
              value={annualDues} 
              onChange={(e) => setAnnualDues(Number(e.target.value))}
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase flex justify-between mb-2">
              <span>Avg. Tenure</span>
              <span className="font-bold text-slate-900 dark:text-white">{averageTenure} Years</span>
            </label>
            <input 
              type="range" 
              min="0.5" 
              max="4" 
              step="0.5"
              value={averageTenure} 
              onChange={(e) => setAverageTenure(Number(e.target.value))}
              className="w-full accent-purple-600"
            />
          </div>
        </Card>

        {/* Results */}
        <div className="md:col-span-2 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-purple-900 text-white">
              <h3 className="text-sm font-bold text-purple-300 uppercase tracking-wider mb-2">Lifetime Value (LTV)</h3>
              <div className="text-4xl font-black text-white tracking-tight mb-1">
                {formatCurrency(model.lifetimeValuePerMember)}
              </div>
              <div className="text-sm text-purple-300">Revenue per new member over {averageTenure} years</div>
            </Card>

            <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Cost Per Acquisition</h3>
              <div className="text-4xl font-black text-red-500 tracking-tight mb-1">
                {formatCurrency(model.costPerNewMember)}
              </div>
              <div className="text-sm text-slate-500">Rush spend per accepted bid</div>
            </Card>
          </div>

          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="text-lg font-bold mb-6">Recruitment Performance</h3>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={model.chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                    >
                      {model.chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800">
                  <div className="font-bold text-sm text-emerald-700 dark:text-emerald-400">Bid Acceptance Rate</div>
                  <div className="font-bold text-emerald-700 dark:text-emerald-400">{model.bidAcceptanceRate.toFixed(1)}%</div>
                </div>

                <div className="flex justify-between items-center p-3 rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-800">
                  <div className="font-bold text-sm text-purple-700 dark:text-purple-400">Total Projected Revenue</div>
                  <div className="font-bold text-purple-700 dark:text-purple-400">{formatCurrency(model.totalNewRevenue)}</div>
                </div>

                <div className="flex justify-between items-center p-3 rounded-lg bg-slate-100 dark:bg-slate-800">
                  <div className="font-bold text-sm text-slate-700 dark:text-slate-300">Rush ROI</div>
                  <div className="font-black text-lg text-slate-900 dark:text-white">+{model.roiPercentage.toFixed(0)}%</div>
                </div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
